import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import { SelectButton } from "primereact/selectbutton";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Message } from "primereact/message";
import {
  apiService,
  InfrastructureRequest,
} from "../../services/apiService";
import { useToast } from "../../contexts/ToastContext";
import { tenantLabelFromInfrastructureRequest } from "./infraDraft";
import { formatTerraformForCopy } from "./terraformPreview";

type QueueFilter = "active" | "all";

const FILTER_OPTIONS: Array<{ label: string; value: QueueFilter }> = [
  { label: "Active project tasks", value: "active" },
  { label: "All queued", value: "all" },
];

function terraformText(row: InfrastructureRequest): string {
  return formatTerraformForCopy(row.terraform_json);
}

function isRowIncomplete(row: InfrastructureRequest): boolean {
  if (typeof row.tasks_incomplete === "boolean") return row.tasks_incomplete;
  if (row.status === "requested") return true;
  if (row.status !== "destruction_requested") return false;
  if (!row.terraform_task_done || !row.backup_task_done) return true;
  if (row.phishing_task_needed && !row.phishing_task_done) return true;
  return false;
}

const Admin: React.FC = () => {
  const toast = useToast();
  const [items, setItems] = useState<InfrastructureRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<InfrastructureRequest | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [filter, setFilter] = useState<QueueFilter>("active");

  const load = useCallback(async () => {
    setLoading(true);
    const res = await apiService.listAdminInfrastructureQueue();
    if (res.success) setItems(res.data || []);
    else {
      setItems([]);
      toast.showError("Admin queue", res.message || "Unable to load queue");
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const activeProjectRefs = useMemo(() => {
    const refs = new Set<string>();
    for (const row of items) {
      if (isRowIncomplete(row)) {
        refs.add(row.project_reference);
      }
    }
    return refs;
  }, [items]);

  const visibleItems = useMemo(() => {
    if (filter === "all") return items;
    // Active: any project with at least one incomplete task keeps all of its
    // queued rows visible (including completed tasks / destroyed siblings).
    return items.filter((row) => activeProjectRefs.has(row.project_reference));
  }, [items, filter, activeProjectRefs]);

  const copyTerraform = async (row: InfrastructureRequest) => {
    try {
      await navigator.clipboard.writeText(terraformText(row));
      toast.showSuccess("Copied", `Terraform JSON for #${row.id} copied`);
    } catch {
      toast.showError("Copy failed", "Unable to copy to clipboard");
    }
  };

  const completeDeploy = (row: InfrastructureRequest) => {
    confirmDialog({
      header: "Mark infrastructure deployed?",
      message: `Mark #${row.id} (${row.project_reference}) as deployed? This notifies Slack and updates status for users.`,
      icon: "pi pi-check-circle",
      acceptLabel: "Mark deployed",
      rejectLabel: "Cancel",
      accept: async () => {
        setBusyId(row.id);
        const res = await apiService.completeInfrastructureDeploy(row.id);
        setBusyId(null);
        if (res.success) {
          toast.showSuccess("Deployed", `Infrastructure #${row.id} marked deployed`);
          void load();
        } else {
          toast.showError("Failed", res.message || "Unable to complete deploy");
        }
      },
    });
  };

  const cancelRequest = (row: InfrastructureRequest) => {
    confirmDialog({
      header: "Cancel infrastructure request?",
      message: `Cancel #${row.id} (${row.project_reference})? This releases its tenant reservation and marks the request canceled.`,
      icon: "pi pi-times-circle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Mark canceled",
      rejectLabel: "Keep",
      accept: async () => {
        setBusyId(row.id);
        const res = await apiService.cancelInfrastructureRequest(row.id);
        setBusyId(null);
        if (res.success) {
          toast.showSuccess("Canceled", `Infrastructure #${row.id} marked canceled`);
          void load();
        } else {
          toast.showError("Failed", res.message || "Unable to cancel request");
        }
      },
    });
  };

  const completeTerraformDestroy = (row: InfrastructureRequest) => {
    confirmDialog({
      header: "Complete terraform destruction?",
      message:
        "This releases non-phishing domains and relay tenants for this infrastructure request, then marks the terraform task done. Continue?",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-warning",
      acceptLabel: "Complete terraform task",
      rejectLabel: "Cancel",
      accept: async () => {
        setBusyId(row.id);
        const res = await apiService.completeInfrastructureTerraformDestroy(row.id);
        setBusyId(null);
        if (res.success) {
          toast.showSuccess(
            "Terraform task done",
            res.data.status === "destroyed"
              ? `Infrastructure #${row.id} fully destroyed`
              : `Terraform destruction completed for #${row.id}`,
          );
          void load();
        } else {
          toast.showError("Failed", res.message || "Unable to complete terraform task");
        }
      },
    });
  };

  const completePhishingRelease = (row: InfrastructureRequest) => {
    confirmDialog({
      header: "Complete phishing release?",
      message:
        "This releases phishing domains and the phishing tenant for this infrastructure request, then marks the phishing task done. Continue?",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-warning",
      acceptLabel: "Complete phishing task",
      rejectLabel: "Cancel",
      accept: async () => {
        setBusyId(row.id);
        const res = await apiService.completeInfrastructurePhishingRelease(row.id);
        setBusyId(null);
        if (res.success) {
          toast.showSuccess(
            "Phishing task done",
            res.data.status === "destroyed"
              ? `Infrastructure #${row.id} fully destroyed`
              : `Phishing release completed for #${row.id}`,
          );
          void load();
        } else {
          toast.showError("Failed", res.message || "Unable to complete phishing task");
        }
      },
    });
  };

  const completeBackupDelete = (row: InfrastructureRequest) => {
    confirmDialog({
      header: "Complete delete backup?",
      message:
        "Confirm that project backups for this infrastructure have been deleted. Continue?",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-warning",
      acceptLabel: "Mark backup deleted",
      rejectLabel: "Cancel",
      accept: async () => {
        setBusyId(row.id);
        const res = await apiService.completeInfrastructureBackupDelete(row.id);
        setBusyId(null);
        if (res.success) {
          toast.showSuccess(
            "Backup task done",
            res.data.status === "destroyed"
              ? `Infrastructure #${row.id} fully destroyed`
              : `Backup delete completed for #${row.id}`,
          );
          void load();
        } else {
          toast.showError("Failed", res.message || "Unable to complete backup task");
        }
      },
    });
  };

  const statusBody = (row: InfrastructureRequest) => {
    const severity =
      row.status === "destruction_requested"
        ? "danger"
        : row.status === "requested"
          ? "warning"
          : row.status === "destroyed"
            ? "success"
            : row.status === "canceled"
              ? "secondary"
              : "info";
    return <Tag value={row.status} severity={severity} />;
  };

  const tasksBody = (row: InfrastructureRequest) => {
    if (row.status === "requested") {
      return (
        <span className="text-600 text-sm">
          Deploy infrastructure (apply terraform)
        </span>
      );
    }
    if (row.status !== "destruction_requested" && row.status !== "destroyed") {
      return "—";
    }

    const tasks: React.ReactNode[] = [
      <div key="tf" className="mb-1">
        {row.terraform_task_done ? (
          <Tag value="Terraform destruction — done" severity="success" />
        ) : (
          <Tag value="Terraform destruction — pending" severity="warning" />
        )}
      </div>,
    ];
    if (row.phishing_task_needed) {
      tasks.push(
        <div key="ph" className="mb-1">
          {row.phishing_task_done ? (
            <Tag value="Phishing release — done" severity="success" />
          ) : (
            <Tag
              value="Phishing release — pending (deregister + release domains/tenant)"
              severity="warning"
            />
          )}
        </div>,
      );
    }
    tasks.push(
      <div key="backup">
        {row.backup_task_done ? (
          <Tag value="Delete backup — done" severity="success" />
        ) : (
          <Tag value="Delete backup — pending" severity="warning" />
        )}
      </div>,
    );
    return <div>{tasks}</div>;
  };

  const actionsBody = (row: InfrastructureRequest) => (
    <div className="flex flex-wrap gap-2">
      <Button
        label="View TF"
        icon="pi pi-eye"
        className="p-button-sm p-button-outlined"
        onClick={() => setDetail(row)}
      />
      <Button
        label="Copy TF"
        icon="pi pi-copy"
        className="p-button-sm p-button-outlined"
        onClick={() => void copyTerraform(row)}
      />
      {row.status === "requested" && (
        <Button
          label="Mark deployed"
          icon="pi pi-check"
          className="p-button-sm p-button-success"
          loading={busyId === row.id}
          onClick={() => completeDeploy(row)}
        />
      )}
      {row.status === "requested" && (
        <Button
          label="Mark canceled"
          icon="pi pi-times"
          className="p-button-sm p-button-danger p-button-outlined"
          loading={busyId === row.id}
          onClick={() => cancelRequest(row)}
        />
      )}
      {row.status === "destruction_requested" && !row.terraform_task_done && (
        <Button
          label="Complete terraform"
          icon="pi pi-check"
          className="p-button-sm p-button-warning"
          loading={busyId === row.id}
          onClick={() => completeTerraformDestroy(row)}
        />
      )}
      {row.status === "destruction_requested" &&
        row.phishing_task_needed &&
        !row.phishing_task_done && (
          <Button
            label="Complete phishing"
            icon="pi pi-check"
            className="p-button-sm p-button-warning"
            loading={busyId === row.id}
            onClick={() => completePhishingRelease(row)}
          />
        )}
      {row.status === "destruction_requested" && !row.backup_task_done && (
        <Button
          label="Complete backup delete"
          icon="pi pi-check"
          className="p-button-sm p-button-warning"
          loading={busyId === row.id}
          onClick={() => completeBackupDelete(row)}
        />
      )}
    </div>
  );

  return (
    <div>
      <ConfirmDialog />
      <div className="flex flex-wrap gap-2 mb-3 align-items-center justify-content-between">
        <h2 className="m-0 text-xl">Infrastructure admin queue</h2>
        <div className="flex flex-wrap gap-2 align-items-center">
          <SelectButton
            value={filter}
            options={FILTER_OPTIONS}
            onChange={(e) => {
              if (e.value) setFilter(e.value);
            }}
            allowEmpty={false}
          />
          <Button
            icon="pi pi-refresh"
            className="p-button-outlined"
            loading={loading}
            onClick={() => void load()}
          />
        </div>
      </div>
      <Message
        className="mb-3 w-full"
        severity="info"
        text="Active project tasks keeps a project visible while any task is still open — including completed tasks under that project. A project leaves Active only when every task for every queued item is done. Destruction requires terraform, phishing (if present), and delete backup."
      />
      <DataTable
        value={visibleItems}
        loading={loading}
        emptyMessage={
          filter === "active"
            ? "No active project tasks"
            : "No infrastructure admin queue items"
        }
        size="small"
        rowGroupMode="subheader"
        groupRowsBy="project_reference"
        rowGroupHeaderTemplate={(data: InfrastructureRequest) => (
          <span className="font-semibold">
            Project {data.project_reference}
            {activeProjectRefs.has(data.project_reference) ? (
              <Tag className="ml-2" value="active" severity="warning" />
            ) : (
              <Tag className="ml-2" value="idle" severity="success" />
            )}
          </span>
        )}
      >
        <Column field="id" header="ID" style={{ width: "5rem" }} />
        <Column field="project_reference" header="Project" />
        <Column header="Status" body={statusBody} />
        <Column
          header="Tenant"
          body={(row: InfrastructureRequest) =>
            tenantLabelFromInfrastructureRequest(row) || row.tenant_id || "—"
          }
        />
        <Column field="created_by_email" header="Requested by" />
        <Column field="created_at" header="Created" />
        <Column header="Tasks" body={tasksBody} style={{ minWidth: "18rem" }} />
        <Column header="Actions" body={actionsBody} style={{ minWidth: "24rem" }} />
      </DataTable>

      <Dialog
        header={`Terraform — infrastructure #${detail?.id || ""}`}
        visible={!!detail}
        style={{ width: "50rem" }}
        onHide={() => setDetail(null)}
        footer={
          detail ? (
            <Button
              label="Copy terraform"
              icon="pi pi-copy"
              onClick={() => void copyTerraform(detail)}
            />
          ) : null
        }
      >
        {detail && (
          <pre
            className="surface-100 p-3 border-round overflow-auto"
            style={{ maxHeight: "28rem" }}
          >
            {terraformText(detail)}
          </pre>
        )}
      </Dialog>
    </div>
  );
};

export default Admin;
